import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import RestoreIcon from '@mui/icons-material/Restore';

import { useHistory, useRedo, useRevert, useUndo } from '../../../queries';
import type { HistoryEntryResponse } from '../../../domain';
import { labelForOperation, relativeTimeLabel } from './historyHelpers';

interface Props {
  sessionId: string | undefined;
  /** Called after a successful revert/undo/redo with the new history
   *  entry (or null for the blank initial state). Parent uses this to
   *  sync the local carousel `activeIndex` to the matching image. */
  onHistoryChange?: (entry: HistoryEntryResponse | null) => void;
}

/**
 * Session-history sidebar. Lists the BE's history entries for the active
 * session newest-first; each row is clickable to revert. Header carries
 * undo / redo buttons backed by `useUndo` / `useRedo` (with `canUndo` /
 * `canRedo` from the history response).
 *
 * When no session exists yet (the first op hasn't been submitted),
 * renders a compact empty state so the panel slot stays mounted.
 */
export const HistoryPanel: React.FC<Props> = ({ sessionId, onHistoryChange }) => {
  const historyQuery = useHistory(sessionId);
  const undoMutation = useUndo(sessionId ?? '');
  const redoMutation = useRedo(sessionId ?? '');
  const revertMutation = useRevert(sessionId ?? '');

  const data = historyQuery.data;
  const entries = data?.history ?? [];
  const currentIndex = data?.currentIndex ?? -1;
  const canUndo = !!sessionId && !!data?.canUndo && !undoMutation.isPending;
  const canRedo = !!sessionId && !!data?.canRedo && !redoMutation.isPending;

  const fireHistoryChange = (newIndex: number) => {
    if (!onHistoryChange) return;
    if (newIndex < 0 || newIndex >= entries.length) {
      onHistoryChange(null);
    } else {
      onHistoryChange(entries[newIndex]);
    }
  };

  const handleUndo = () => {
    if (!sessionId) return;
    undoMutation.mutate(undefined, {
      onSuccess: response => fireHistoryChange(response.newCurrentIndex),
    });
  };
  const handleRedo = () => {
    if (!sessionId) return;
    redoMutation.mutate(undefined, {
      onSuccess: response => fireHistoryChange(response.newCurrentIndex),
    });
  };
  const handleRevert = (historyIndex: number) => {
    if (!sessionId) return;
    revertMutation.mutate(
      { historyIndex },
      {
        onSuccess: () => fireHistoryChange(historyIndex),
      },
    );
  };

  // Show entries newest-first so the latest op is at the top of the list.
  // The entries array from the BE is index-ascending; reverse for display.
  const displayed = [...entries].reverse();

  return (
    <Box
      sx={{
        flexShrink: 0,
        borderTop: theme => `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '40%',
        minHeight: 120,
      }}
      aria-label="History panel"
    >
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{ px: 2, pt: 1.5, pb: 0.5 }}
      >
        <HistoryIcon fontSize="small" />
        <Typography variant="overline" color="text.secondary" sx={{ flex: 1 }}>
          History
        </Typography>
        <Tooltip title={canUndo ? 'Undo' : 'Nothing to undo'}>
          <span>
            <IconButton size="small" onClick={handleUndo} disabled={!canUndo}>
              <UndoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={canRedo ? 'Redo' : 'Nothing to redo'}>
          <span>
            <IconButton size="small" onClick={handleRedo} disabled={!canRedo}>
              <RedoIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 1.5 }}>
        {!sessionId ? (
          <Typography variant="caption" color="text.secondary">
            History appears here after the first operation.
          </Typography>
        ) : historyQuery.isLoading ? (
          <Stack alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={20} />
          </Stack>
        ) : historyQuery.isError ? (
          <Alert severity="warning" sx={{ fontSize: 12 }}>
            Couldn't load history.
          </Alert>
        ) : displayed.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No operations yet. Run Generate or upload an image to start.
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {displayed.map(entry => {
              const isCurrent = entry.historyIndex === currentIndex;
              return (
                <HistoryRow
                  key={entry.operationId}
                  entry={entry}
                  isCurrent={isCurrent}
                  disabled={revertMutation.isPending}
                  onRevert={() => handleRevert(entry.historyIndex)}
                />
              );
            })}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

interface HistoryRowProps {
  entry: HistoryEntryResponse;
  isCurrent: boolean;
  disabled: boolean;
  onRevert: () => void;
}

const HistoryRow: React.FC<HistoryRowProps> = ({ entry, isCurrent, disabled, onRevert }) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="center"
    sx={{
      px: 1,
      py: 0.5,
      borderRadius: 1,
      bgcolor: isCurrent ? 'action.selected' : 'transparent',
      border: theme =>
        isCurrent ? `1px solid ${theme.palette.primary.main}` : '1px solid transparent',
    }}
  >
    {entry.imageUrl ? (
      <Box
        component="img"
        src={entry.imageUrl}
        alt=""
        sx={{
          width: 36,
          height: 36,
          objectFit: 'cover',
          borderRadius: 0.5,
          flexShrink: 0,
          bgcolor: 'action.hover',
        }}
      />
    ) : (
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 0.5,
          flexShrink: 0,
          bgcolor: 'action.hover',
        }}
      />
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="body2" noWrap>
        {labelForOperation(entry.operationType)}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {relativeTimeLabel(entry.timestamp)}
      </Typography>
    </Box>
    <Tooltip title={isCurrent ? 'Current state' : 'Revert to this step'}>
      <span>
        <IconButton
          size="small"
          onClick={onRevert}
          disabled={disabled || isCurrent}
        >
          <RestoreIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  </Stack>
);
